import { keyframes } from '@emotion/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/core/hooks/useAuth';
import { LoginButtons } from './LoginButtons';

const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export function LoginPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  if (!authLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #FAFAFA 0%, #F0EFF5 100%)',
      }}
    >
      {/* 背景デコレーション */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-15%',
          width: '50vw',
          height: '50vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(138,43,226,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-10%',
          width: '40vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,215,0,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* ロゴ + タイトル */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 5,
          position: 'relative',
          animation: `${fadeInUp} 0.7s ease-out`,
        }}
      >
        <Box
          component="img"
          src="/logo.gif"
          alt="RunPort コレクト"
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2.5,
            display: 'block',
          }}
        />
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            background: GRADIENT,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          RunPort コレクト
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          マラソン完走証をデジタル記録として保存
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 300, width: '100%', animation: `${fadeInUp} 0.5s ease-out` }}>
          {error}
        </Alert>
      )}

      <Box sx={{ animation: `${fadeInUp} 0.7s ease-out 0.15s both` }}>
        <LoginButtons returnUrl="/dashboard" />
      </Box>

      <Box sx={{ mt: 5, animation: `${fadeInUp} 0.7s ease-out 0.3s both` }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', lineHeight: 1.6 }}
        >
          ログインすることで、
          <Box
            component="a"
            href="https://2dot99.com/tos.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'text.secondary', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            利用規約
          </Box>
          と
          <Box
            component="a"
            href="https://2dot99.com/privacypolicy.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'text.secondary', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            プライバシーポリシー
          </Box>
          に同意したものとみなされます。
        </Typography>
      </Box>
    </Box>
  );
}
